import { useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeft,
  CreditCard,
  Shield,
  Check,
  Loader2,
  AlertCircle,
  Calendar,
  Star
} from 'lucide-react'
import { 
  commerceApi, 
  formatPrice, 
  getBillingPeriodLabel,
  type PricingResponse,
  type PaymentMethod 
} from '@/api/commerce'

export function CheckoutPage() {
  const { productId } = useParams<{ productId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const pricingIdParam = searchParams.get('pricing')
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(
    pricingIdParam ? Number(pricingIdParam) : null
  )
  const [paymentMethod] = useState<PaymentMethod>('credit_card')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Mock 카드 정보
  const [cardNumber, setCardNumber] = useState('4242424242424242')
  const [cardExpiry, setCardExpiry] = useState('12/28')
  const [cardCvc, setCardCvc] = useState('123')

  // 상품 정보 조회
  const { data: product, isLoading, error: productError } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => commerceApi.getProduct(Number(productId)),
    enabled: !!productId,
  })

  // 주문 생성 및 결제 처리
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!product || !selectedPricingId) {
        throw new Error('상품 및 가격을 선택해주세요')
      }

      // 1. 주문 생성
      const order = await commerceApi.createOrder({
        productId: product.id,
        pricingId: selectedPricingId,
        quantity: 1,
      })

      // 2. 결제 처리
      const result = await commerceApi.processPayment({
        orderId: order.id,
        paymentMethod,
        cardNumber,
        cardExpiry,
        cardCvc,
      })

      return result
    },
    onSuccess: (result) => {
      navigate('/subscription/success', {
        state: {
          orderNumber: result.order.orderNumber,
          subscriptionId: result.subscriptionId,
          productName: product?.name,
        },
      })
    },
    onError: (error: Error) => {
      setError(error.message)
      setIsProcessing(false)
    },
  })

  const handleCheckout = async () => {
    setError(null)
    setIsProcessing(true)
    checkoutMutation.mutate()
  }

  // 선택된 가격 옵션
  const selectedPricing = product?.pricingOptions.find(
    (p) => p.id === selectedPricingId
  ) || product?.pricingOptions.find((p) => p.isDefault) || product?.pricingOptions[0]

  // 선택된 pricing ID 설정
  if (product && !selectedPricingId && selectedPricing) {
    setSelectedPricingId(selectedPricing.id)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
        <p className="text-gray-600">상품 정보를 불러오는 중...</p>
      </div>
    )
  }

  if (productError || !product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-gray-900 font-medium mb-2">상품을 찾을 수 없습니다</p>
        <Link to="/learn" className="text-primary-600 hover:underline">
          학습 목록으로 돌아가기
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>뒤로가기</span>
      </button>

      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
        구독 결제
      </h1>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Payment Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* 가격 옵션 선택 */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              구독 플랜 선택
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {product.pricingOptions.map((pricing) => (
                <PricingOptionCard
                  key={pricing.id}
                  pricing={pricing}
                  isSelected={selectedPricingId === pricing.id}
                  onSelect={() => setSelectedPricingId(pricing.id)}
                />
              ))}
            </div>
          </div>

          {/* 결제 정보 (Mock) */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary-600" />
              결제 정보
            </h2>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>테스트 모드:</strong> 실제 결제가 진행되지 않습니다. 아래 정보는 데모용입니다.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label">카드 번호</label>
                <input
                  type="text"
                  className="input"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="0000 0000 0000 0000"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">만료일</label>
                  <input
                    type="text"
                    className="input"
                    value={cardExpiry}
                    onChange={(e) => setCardExpiry(e.target.value)}
                    placeholder="MM/YY"
                  />
                </div>
                <div>
                  <label className="label">CVC</label>
                  <input
                    type="text"
                    className="input"
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value)}
                    placeholder="123"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <Shield className="w-5 h-5 text-green-600" />
            <span>결제 정보는 안전하게 암호화되어 처리됩니다.</span>
          </div>
        </div>

        {/* Right: Order Summary */}
        <div className="lg:col-span-1">
          <div className="card p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              주문 요약
            </h2>

            {/* Product Info */}
            <div className="border-b border-gray-200 pb-4 mb-4">
              <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
              {product.shortDescription && (
                <p className="text-sm text-gray-500">{product.shortDescription}</p>
              )}
            </div>

            {/* Pricing Details */}
            {selectedPricing && (
              <div className="space-y-3 border-b border-gray-200 pb-4 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>{selectedPricing.name}</span>
                  <span>{formatPrice(selectedPricing.price, selectedPricing.currency)}</span>
                </div>
                {selectedPricing.originalPrice && (
                  <div className="flex justify-between text-gray-400">
                    <span>정가</span>
                    <span className="line-through">
                      {formatPrice(selectedPricing.originalPrice, selectedPricing.currency)}
                    </span>
                  </div>
                )}
                {selectedPricing.discountPercentage && (
                  <div className="flex justify-between text-green-600">
                    <span>할인</span>
                    <span>-{selectedPricing.discountPercentage}%</span>
                  </div>
                )}
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center text-lg font-semibold mb-6">
              <span>총 결제금액</span>
              <span className="text-primary-600">
                {selectedPricing
                  ? formatPrice(selectedPricing.price, selectedPricing.currency)
                  : '-'}
              </span>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={isProcessing || !selectedPricingId}
              className="btn-primary w-full py-4 text-lg"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  결제 처리중...
                </>
              ) : (
                '결제하기'
              )}
            </button>

            {/* Subscription Notice */}
            <p className="text-xs text-gray-500 mt-4 text-center">
              결제 후 즉시 이용 가능합니다. 구독은 자동 갱신되며, 언제든 취소할 수 있습니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sub Components
// ============================================================================

interface PricingOptionCardProps {
  pricing: PricingResponse
  isSelected: boolean
  onSelect: () => void
}

function PricingOptionCard({ pricing, isSelected, onSelect }: PricingOptionCardProps) {
  const hasDiscount = pricing.originalPrice && pricing.discountPercentage

  return (
    <button
      onClick={onSelect}
      className={`relative p-4 rounded-xl border-2 transition-all text-left w-full ${
        isSelected
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      {/* Popular Badge */}
      {pricing.isDefault && (
        <div className="absolute -top-3 left-4 px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded-full flex items-center gap-1">
          <Star className="w-3 h-3" />
          추천
        </div>
      )}

      {/* Selection Indicator */}
      <div
        className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
          isSelected
            ? 'border-primary-500 bg-primary-500'
            : 'border-gray-300'
        }`}
      >
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Plan Name */}
      <h3 className="font-semibold text-gray-900 mb-1 pr-8">{pricing.name}</h3>
      
      {/* Billing Period */}
      <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
        <Calendar className="w-4 h-4" />
        <span>{getBillingPeriodLabel(pricing.billingPeriod)}</span>
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900">
          {formatPrice(pricing.price, pricing.currency)}
        </span>
        {hasDiscount && (
          <span className="text-sm text-gray-400 line-through">
            {formatPrice(pricing.originalPrice!, pricing.currency)}
          </span>
        )}
      </div>

      {/* Discount Badge */}
      {hasDiscount && (
        <span className="inline-block mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-sm">
          {pricing.discountPercentage}% 할인
        </span>
      )}
    </button>
  )
}





















